import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";

const ViewPurchaseOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (selectedFacilityId && orderId) {
      fetchPurchaseOrderDetails();
    }
  }, [selectedFacilityId, orderId]);

  const fetchPurchaseOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/purchase-orders/details/${selectedFacilityId}/${orderId}`,
        'GET'
      );

      if (response.success && response.data) {
        setPurchaseOrder(response.data.data);
      } else {
        toastify(response.error || 'Failed to fetch purchase order details', 'error');
        console.error('Error fetching purchase order details:', response);
      }
    } catch (error) {
      toastify('Error fetching purchase order details', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (value) =>
    value !== null && value !== undefined
      ? `KES ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 'KES 0.00';

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      
      return `${day} ${month}, ${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Status badge display
  const renderStatusBadge = (status) => {
    const statusClasses = {
      'pending approval': 'bg-warning',
      'approved': 'bg-success',
      'rejected': 'bg-danger',
      'sent': 'bg-info',
      'received': 'bg-primary',
      'completed': 'bg-success',
      'canceled': 'bg-secondary'
    };

    const badgeClass = statusClasses[status?.toLowerCase()] || 'bg-secondary';

    return (
      <span className={`badge ${badgeClass}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
      </span>
    );
  };

  // If no facility is selected, show a prompt
  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view purchase order details.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header & Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/procurement_management/purchase_orders">
                    Purchase Orders
                  </Link>
                </li>
                <li className="breadcrumb-item">View Purchase Order</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <Link to="/facility/procurement_management/purchase_orders">
                  <i className="ti ti-arrow-narrow-left"></i> Back
                </Link>
              </div>
              <div className="d-flex gap-2">
                {purchaseOrder && !['completed', 'canceled', 'rejected'].includes(purchaseOrder.status) && (
                  <button className="btn btn-primary" onClick={() => navigate(`/facility/procurement_management/purchase_order/edit/${orderId}`)}>
                    <i className="ti ti-edit me-1"></i> Edit
                  </button>
                )}
                <button className="btn btn-outline-primary" onClick={() => window.print()}>
                  <i className="ti ti-printer me-1"></i> Print
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          ) : purchaseOrder ? (
            <div className="card">
              <div className="card-header">
                <h5>Purchase Order Details</h5>
              </div>
              <div className="card-body">
                {/* PO Header Information */}
                <div className="row mb-4">
                  <div className="col-md-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h3 className="fw-bold mb-0">PO #{purchaseOrder.poNumber}</h3>
                      <div>{renderStatusBadge(purchaseOrder.status)}</div>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  {/* Main PO Info */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Purchase Order Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">PO Number:</td>
                              <td>{purchaseOrder.poNumber || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">PR Number:</td>
                              <td>{purchaseOrder.prNumber || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Department:</td>
                              <td>{purchaseOrder.department || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Currency:</td>
                              <td>{purchaseOrder.currency || 'KES'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Date Created:</td>
                              <td>{formatDate(purchaseOrder.date)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Budget:</td>
                              <td>{purchaseOrder.budget || 'N/A'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Supplier & Approval Info */}
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Supplier Information</h6>
                      </div>
                      <div className="card-body">
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Supplier Name:</td>
                              <td>
                                {purchaseOrder.supplierInfo ? (
                                  <Link to={`/facility/procurement_management/supplier/view/${purchaseOrder.supplierInfo?.supplierId}`}>
                                    {purchaseOrder.supplierInfo.name}
                                  </Link>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Email:</td>
                              <td>{purchaseOrder.supplierInfo?.email || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Phone:</td>
                              <td>{purchaseOrder.supplierInfo?.phone || 'N/A'}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Status:</td>
                              <td>
                                <span className={`badge ${purchaseOrder.supplierInfo?.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                  {purchaseOrder.supplierInfo?.status?.charAt(0).toUpperCase() + purchaseOrder.supplierInfo?.status?.slice(1) || 'N/A'}
                                </span>
                              </td>
                            </tr>
                            {/* <tr>
                              <td className="fw-bold">Current Step:</td>
                              <td>{purchaseOrder.currentStep || 'N/A'}</td>
                            </tr> */}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Internal Notes</h6>
                      </div>
                      <div className="card-body">
                        <p>{purchaseOrder.internalNotes || 'No internal notes available.'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Supplier Notes</h6>
                      </div>
                      <div className="card-body">
                        <p>{purchaseOrder.supplierNotes || 'No supplier notes available.'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div className="row mb-4">
                  <div className="col-md-12">
                    <div className="card">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">Order Items</h6>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Description</th>
                                <th>Quantity</th>
                                <th>Unit</th>
                                <th>Unit Price</th>
                                <th>Tax Rate (%)</th>
                                <th>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {purchaseOrder.items && purchaseOrder.items.length > 0 ? (
                                purchaseOrder.items.map((item, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item.itemDescription}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.unitOfMeasure}</td>
                                    <td>{formatCurrency(item.unitPrice)}</td>
                                    <td>{item.taxRate}%</td>
                                    <td>{formatCurrency(item.totalPrice)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="7" className="text-center">No items found</td>
                                </tr>
                              )}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan="5"></td>
                                <td className="fw-bold">Subtotal:</td>
                                <td>{formatCurrency(purchaseOrder.subtotal)}</td>
                              </tr>
                              <tr>
                                <td colSpan="5"></td>
                                <td className="fw-bold">Tax Total:</td>
                                <td>{formatCurrency(purchaseOrder.taxTotal)}</td>
                              </tr>
                              <tr>
                                <td colSpan="5"></td>
                                <td className="fw-bold">Grand Total:</td>
                                <td className="fw-bold">{formatCurrency(purchaseOrder.grandTotal)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments Section */}
                {purchaseOrder.attachments && purchaseOrder.attachments.length > 0 && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="card">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Attachments</h6>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table">
                              <thead>
                                <tr>
                                  <th>File Name</th>
                                  <th>File Type</th>
                                  <th>Upload Date</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {purchaseOrder.attachments.map((attachment, index) => (
                                  <tr key={index}>
                                    <td>{attachment.name}</td>
                                    <td>{attachment.fileType}</td>
                                    <td>{formatDate(attachment.uploadDate)}</td>
                                    <td>
                                      <a
                                        href={attachment.filePath}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-sm btn-outline-primary"
                                      >
                                        <i className="ti ti-download me-1"></i> Download
                                      </a>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center">
                <p>Purchase order not found or unable to fetch details.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ViewPurchaseOrder;